import Box from "@mui/material/Box";
import { GridRowModes, DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { Delete, Get, Post, Put } from "../../Common/WebRequest";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { slice, getStorageItem } from "../../Common/Utility";
import columnBuilder from "./columnBuilder";
import EditToolbar from "./EditToolbar";
import TeacherDialog from "./TeacherDialog";

export default function TeachersGrid() {
  const [teachersData, setTeachersData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [TeacherId, setTeacherId] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      const admin = await getStorageItem("login", "isAdmin");
      setIsAdmin(!!admin);
    })();
  }, []);

  // Delete modal state
  const [type, setType] = useState(null);
  const [id, setId] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, [reload]);

  const loadData = async () => {
    setIsLoading(true);
    clearMessages(); // This will clear both error and success messages on reload

    try {
      const [teachersRes, regionsRes, branchesRes] = await Promise.all([
        Get("teacher"),
        Get("region"),
        Get("branch"),
      ]);

      if (!teachersRes.isSuccess) throw teachersRes;
      if (!regionsRes.isSuccess) throw regionsRes;
      if (!branchesRes.isSuccess) throw branchesRes;

      setTeachersData(teachersRes.result);
      setRegionData(regionsRes.result);
      setBranchData(branchesRes.result);
    } catch (err) {
      handleError(err, "data loading");
      setTeachersData([]); // Reset to empty on error
      setRegionData([]);
      setBranchData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (err, operation) => {
    console.error(`Error during ${operation}:`, err);
    setError(err.Status ? `${err.Status}/${err.Error}` : err.message);
    setSuccessMsg("");
  };

  const clearMessages = () => {
    setError("");
    setSuccessMsg("");
  };

  const handleEditClick = (id) => () => {
    clearMessages();
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "teacherName" },
    });
  };

  const handleSaveClick = (id) => async (event) => {
    event.stopPropagation();
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const teacherToDelete = teachersData.find((x) => x.id === id);
    setDeleteMessage(
      `Möchten Sie „${teacherToDelete.teacherName}" wirklich löschen?`
    );
    setType("");
    setId(id);
    setDisplayConfirmationModal(true);
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = teachersData.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setTeachersData(teachersData.filter((row) => row.id !== id));
    }
  };

  async function processRowUpdate(newRow) {
    try {
      const updatedRow = { ...newRow, isNew: false };
      await DoSaveApi(newRow, updatedRow);
      return updatedRow;
    } catch (err) {
      handleError(err, "row update");
      throw err;
    }
  }

  async function DoSaveApi(newRow, updatedRow) {
    clearMessages();
    const originalData = [...teachersData];

    try {
      setTeachersData(
        teachersData.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      const res =
        updatedRow.id === 0
          ? await Post("teacher", { ...updatedRow, isNew: true })
          : await Put("teacher", updatedRow.id, newRow);

      if (!res.isSuccess) throw res;

      setSuccessMsg(
        updatedRow.id === 0
          ? "Die neue Lehrkräfte wurde hinzugefügt."
          : "Die Lehrkräfte wurde aktualisiert."
      );

      // Add delay before reload to allow message to be shown
      setTimeout(() => {
        setReload(!reload);
      }, 1000);
    } catch (err) {
      handleError(err, "saving");
      setTeachersData(originalData); // Restore original data
      loadData(); // Ensure data consistency
      throw err; // Re-throw to trigger grid error handling
    }
  }

  async function handleAfterDeleteClick() {
    setDisplayConfirmationModal(false);
    try {
      const res = await Delete("teacher", id);
      if (!res.isSuccess) throw res;

      setSuccessMsg("Die Lehrkräfte wurde erfolgreich gelöscht.");
      setReload(!reload);
    } catch (err) {
      handleError(err, "deletion");
    }
  }

  const handleAddUpdateTeacher = (teacherId) => {
    setTeacherId(teacherId);
    setIsDialogOpen(true);
  };

  const handleOnRowDoubleClick = (params) => {
    if (!isAdmin || !params.row) return;
    handleAddUpdateTeacher(params.row.id);
  };

  const handleSaveTeacherData = () => {
    setReload(!reload);
  };

  const columns = columnBuilder({
    regionData,
    branchData,
    rowModesModel,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    isAdmin,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        height: 800,
        width: "95%",
        "& .actions": { color: "text.secondary" },
        "& .textPrimary": { color: "text.primary" },
      }}
    >
      <br />
      <h2>Lehrkräfte</h2>
      <br />
      {error && !error.includes("No Results") && (
        <div className="alert alert-danger" role="alert">
          {slice(error, 200, "...")}
        </div>
      )}
      {!error && successMsg && (
        <div className="alert alert-success" role="alert">
          {slice(successMsg, 100, "...")}
        </div>
      )}
      <br />
      <Box
        sx={{
          width: "100%",
          height: "700px",
          "& .Mui-even": { backgroundColor: "#f3f7ff" },
          "& .Mui-odd": { backgroundColor: "#EEEEEE" },
          "& .MuiDataGrid-cell:hover": { backgroundColor: "#80b3ff" },
          "&& .MuiDataGrid-row:hover": { backgroundColor: "#cce0ff" },
        }}
      >
        <TeacherDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSaveTeacherData}
          reload={reload}
          TeacherId={TeacherId}
        />
        <DataGrid
          rowSelection={false}
          density="compact"
          rows={teachersData}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          onRowEditStart={(params, event) => {
            if (!isAdmin) event.defaultMuiPrevented = true;
          }}
          onRowEditStop={(params, event) => (event.defaultMuiPrevented = true)}
          onRowDoubleClick={handleOnRowDoubleClick}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) => handleError(err, "grid update")}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              setTeachersData,
              setRowModesModel,
              handleAddUpdateTeacher,
              isAdmin,
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
          }
          initialState={{
            pagination: { paginationModel: { pageSize: 100, page: 0 } },
            filter: {
              filterModel: {
                items: [
                  { field: "teacherName", operator: "contains", value: "" },
                ],
              },
            },
          }}
        />
      </Box>
      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={handleAfterDeleteClick}
        hideModal={() => setDisplayConfirmationModal(false)}
        type={type}
        id={id}
        message={deleteMessage}
      />
    </Box>
  );
}
